import React from "react";
import {useSelector} from "react-redux";

import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {buildFriendlyPromotionSlug} from "../../promotion/helpers/build_friendly_promotion_slug";
import {RegionType} from "../../region/types/RegionType";
import {PRICES_CITIES, PRICES_CITIES_SLUGS} from "../../report/price/constants/prices_cities";
import {offerUrlBuilder} from "../helpers/friendly_offer_list/offer_url_builder";
import {getOfferTypeNamePlural} from "../helpers/OfferType";
import {IRegionAncestor} from "../list/actions/offer_list_selected_data_actions";
import {IFriendlyQuery} from "../list/types/friendly_query";
import {OfferType} from "../types/OfferType";
import {getRegionDataForVendorListing} from "../utils/get_region_data_for_vendor_listing";
import {OfferLocationLinksItem} from "./OfferListLocationLinksItem";

export interface IOfferLocationLinksOtherOffersProps {
    region: {
        id: number;
        name: string;
        name_declension_what: string;
        name_declension_where: string;
        slug: string;
        stats?: {
            properties_houses_count_luxury: number;
            properties_flats_count_luxury: number;
            promotions_count_total: number;
            properties_houses_count_cheap: number;
            properties_flats_count_cheap: number;
            properties_commercials_count_cheap: number;
            properties_houses_count_ready: number;
            properties_flats_count_ready: number;
            properties_commercial_count_ready: number;
            region_type_city: IRegionAncestor | null;
        };
        short_name: string;
        type: RegionType;
    };
    offerType: OfferType | null;
    variant?: string;
}

interface IStatsLuxury {
    properties_houses_count_luxury: number;
    properties_flats_count_luxury: number;
    promotions_count_total: number;
}

export const getLuxuryOfferCount = (offerType: OfferType | null, stats: IStatsLuxury): number => {
    const {properties_houses_count_luxury: houseCount, properties_flats_count_luxury: flatCount} = stats;
    if (!offerType) {
        // null is only present for mixed offer type - when flat + house is selected
        return flatCount + houseCount;
    }
    switch (offerType) {
        case OfferType.HOUSE:
            return houseCount;
        case OfferType.FLAT:
            return flatCount;
        default:
            return 0;
    }
};

interface IStatsCheap {
    properties_houses_count_cheap: number;
    properties_flats_count_cheap: number;
    properties_commercials_count_cheap: number;
}
export const getCheapOffersCount = (offerType: OfferType | null, stats: IStatsCheap): number => {
    const {properties_commercials_count_cheap: commercialsCount, properties_flats_count_cheap: flatCount, properties_houses_count_cheap: houseCount} = stats;

    switch (offerType) {
        // null is only present for mixed offer type - when flat + house is selected
        case null:
            return flatCount + houseCount + commercialsCount;
        case OfferType.HOUSE:
            return houseCount;
        case OfferType.FLAT:
            return flatCount;
        default:
            return 0;
    }
};

export const getReadyOfferCount = (offerType: OfferType | null, readyHousesCount: number, readyFlatsCount: number, readyCommercialCount: number) => {
    if (!offerType) {
        // null is only present for mixed offer type - when flat + house is selected
        return readyFlatsCount + readyHousesCount;
    }

    switch (offerType) {
        case OfferType.HOUSE:
            return readyHousesCount;
        case OfferType.FLAT:
            return readyFlatsCount;
        case OfferType.COMMERCIAL:
            return readyCommercialCount;
        default:
            return 0;
    }
};

export const OfferLocationLinksOtherOffers = React.memo((props: IOfferLocationLinksOtherOffersProps) => {
    const {region, offerType} = props;

    const parentRegions = useSelector((state: IRPStore) => state.offerList.regionPath);

    const inDistrictOrNeighborhood = [RegionType.DISTRICT, RegionType.NEIGHBORHOOD].includes(region.type);
    const canUseCitySlug = inDistrictOrNeighborhood && Boolean(region.stats?.region_type_city?.slug);
    const isCheapOrReady = props.variant && ["cheap", "ready"].includes(props.variant);
    const regionSlug = region.stats && canUseCitySlug && !isCheapOrReady ? region.stats.region_type_city?.slug : region.slug;

    const regionFriendlySlug = (query: Partial<IFriendlyQuery>) =>
        offerUrlBuilder({
            type: offerType,
            region: regionSlug,
            ...query
        }).friendlySlug;
    const friendlyOfferListUrl = (query: Partial<IFriendlyQuery>) => rpAppLink.offer.listFriendly({friendlySlug: regionFriendlySlug(query)});
    const priceSortLink = friendlyOfferListUrl({price_lower_than_average: true});
    const constructionDateReady = friendlyOfferListUrl({construction_end_date: "0"});
    const promotionLink =
        region &&
        regionSlug &&
        rpAppLink.promotions.listFriendly({
            friendlySlug: buildFriendlyPromotionSlug({
                region: regionSlug
            })
        });
    const regionDataForVendorListing = getRegionDataForVendorListing(region, parentRegions);
    const luxuryLink = friendlyOfferListUrl({is_luxury: true});
    const priceReportExist =
        PRICES_CITIES_SLUGS.includes(region.slug) || (region.stats?.region_type_city && PRICES_CITIES_SLUGS.includes(region.stats.region_type_city.slug));
    const regionWithPrices =
        priceReportExist && PRICES_CITIES_SLUGS.includes(region.slug)
            ? PRICES_CITIES.find((city) => city.slug === region.slug)
            : PRICES_CITIES.find((city) => city.slug === region.stats?.region_type_city?.slug);
    const regionName = canUseCitySlug && !isCheapOrReady ? region.stats?.region_type_city?.name : region.name;

    return (
        <>
            {props.variant === "cheap" ? (
                <OfferLocationLinksItem
                    disableSelfLinking
                    linkText={`Tanie ${offerType ? getOfferTypeNamePlural(offerType) : "nieruchomości"} ${regionName}`}
                    url={priceSortLink}
                />
            ) : null}

            {props.variant === "ready" ? (
                <OfferLocationLinksItem
                    disableSelfLinking
                    linkText={`Gotowe ${offerType ? getOfferTypeNamePlural(offerType) : "nieruchomości"} ${regionName}`}
                    url={constructionDateReady}
                />
            ) : null}

            {/*<OfferLocationLinksItem disableSelfLinking linkText="Mieszkania z dopłatą MDM" url={rpAppPath.offerMdmList} />*/}

            {props.variant === "promotion" && promotionLink ? (
                <OfferLocationLinksItem disableSelfLinking linkText={`Oferty promocyjne ${regionName}`} url={promotionLink} />
            ) : null}

            {props.variant === "price" && regionWithPrices && offerType === OfferType.FLAT ? (
                <OfferLocationLinksItem
                    disableSelfLinking
                    linkText={`Ceny mieszkań ${regionWithPrices.name}`}
                    url={rpAppLink.prices.flats.city({regionSlug: regionWithPrices.slug})}
                />
            ) : null}

            {props.variant === "luxury" ? (
                <OfferLocationLinksItem
                    disableSelfLinking
                    linkText={`Luksusowe ${offerType ? getOfferTypeNamePlural(offerType) : "nieruchomości"} ${regionName}`}
                    url={luxuryLink}
                />
            ) : null}

            {props.variant === "vendors" && regionDataForVendorListing ? (
                <OfferLocationLinksItem disableSelfLinking linkText={`Deweloperzy ${regionDataForVendorListing.label}`} url={regionDataForVendorListing.url} />
            ) : null}
        </>
    );
});
import React from "react";
import {useSelector} from "react-redux";

import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {buildFriendlyPromotionSlug} from "../../promotion/helpers/build_friendly_promotion_slug";
import {RegionType} from "../../region/types/RegionType";
import {PRICES_CITIES, PRICES_CITIES_SLUGS} from "../../report/price/constants/prices_cities";
import {offerUrlBuilder} from "../helpers/friendly_offer_list/offer_url_builder";
import {getOfferTypeNamePlural} from "../helpers/OfferType";
import {IRegionAncestor} from "../list/actions/offer_list_selected_data_actions";
import {IFriendlyQuery} from "../list/types/friendly_query";
import {OfferType} from "../types/OfferType";
import {getRegionDataForVendorListing} from "../utils/get_region_data_for_vendor_listing";
import {OfferLocationLinksItem} from "./OfferListLocationLinksItem";

export interface IOfferLocationLinksOtherOffersProps {
    region: {
        id: number;
        name: string;
        name_declension_what: string;
        name_declension_where: string;
        slug: string;
        stats?: {
            properties_houses_count_luxury: number;
            properties_flats_count_luxury: number;
            promotions_count_total: number;
            properties_houses_count_cheap: number;
            properties_flats_count_cheap: number;
            properties_commercials_count_cheap: number;
            properties_houses_count_ready: number;
            properties_flats_count_ready: number;
            properties_commercial_count_ready: number;
            region_type_city: IRegionAncestor | null;
        };
        short_name: string;
        type: RegionType;
    };
    offerType: OfferType | null;
    variant?: string;
}

interface IStatsLuxury {
    properties_houses_count_luxury: number;
    properties_flats_count_luxury: number;
    promotions_count_total: number;
}

export const getLuxuryOfferCount = (offerType: OfferType | null, stats: IStatsLuxury): number => {
    const {properties_houses_count_luxury: houseCount, properties_flats_count_luxury: flatCount} = stats;
    if (!offerType) {
        // null is only present for mixed offer type - when flat + house is selected
        return flatCount + houseCount;
    }
    switch (offerType) {
        case OfferType.HOUSE:
            return houseCount;
        case OfferType.FLAT:
            return flatCount;
        default:
            return 0;
    }
};

interface IStatsCheap {
    properties_houses_count_cheap: number;
    properties_flats_count_cheap: number;
    properties_commercials_count_cheap: number;
}
export const getCheapOffersCount = (offerType: OfferType | null, stats: IStatsCheap): number => {
    const {properties_commercials_count_cheap: commercialsCount, properties_flats_count_cheap: flatCount, properties_houses_count_cheap: houseCount} = stats;

    switch (offerType) {
        // null is only present for mixed offer type - when flat + house is selected
        case null:
            return flatCount + houseCount + commercialsCount;
        case OfferType.HOUSE:
            return houseCount;
        case OfferType.FLAT:
            return flatCount;
        default:
            return 0;
    }
};

export const getReadyOfferCount = (offerType: OfferType | null, readyHousesCount: number, readyFlatsCount: number, readyCommercialCount: number) => {
    if (!offerType) {
        // null is only present for mixed offer type - when flat + house is selected
        return readyFlatsCount + readyHousesCount;
    }

    switch (offerType) {
        case OfferType.HOUSE:
            return readyHousesCount;
        case OfferType.FLAT:
            return readyFlatsCount;
        case OfferType.COMMERCIAL:
            return readyCommercialCount;
        default:
            return 0;
    }
};

export const OfferLocationLinksOtherOffers = React.memo((props: IOfferLocationLinksOtherOffersProps) => {
    const {region, offerType} = props;

    const parentRegions = useSelector((state: IRPStore) => state.offerList.regionPath);

    const inDistrictOrNeighborhood = [RegionType.DISTRICT, RegionType.NEIGHBORHOOD].includes(region.type);
    const canUseCitySlug = inDistrictOrNeighborhood && Boolean(region.stats?.region_type_city?.slug);
    const isCheapOrReady = props.variant && ["cheap", "ready"].includes(props.variant);
    const regionSlug = region.stats && canUseCitySlug && !isCheapOrReady ? region.stats.region_type_city?.slug : region.slug;

    const regionFriendlySlug = (query: Partial<IFriendlyQuery>) =>
        offerUrlBuilder({
            type: offerType,
            region: regionSlug,
            ...query
        }).friendlySlug;
    const friendlyOfferListUrl = (query: Partial<IFriendlyQuery>) => rpAppLink.offer.listFriendly({friendlySlug: regionFriendlySlug(query)});
    const priceSortLink = friendlyOfferListUrl({price_lower_than_average: true});
    const constructionDateReady = friendlyOfferListUrl({construction_end_date: "0"});
    const promotionLink =
        region &&
        regionSlug &&
        rpAppLink.promotions.listFriendly({
            friendlySlug: buildFriendlyPromotionSlug({
                region: regionSlug
            })
        });
    const regionDataForVendorListing = getRegionDataForVendorListing(region, parentRegions);
    const luxuryLink = friendlyOfferListUrl({is_luxury: true});
    const priceReportExist =
        PRICES_CITIES_SLUGS.includes(region.slug) || (region.stats?.region_type_city && PRICES_CITIES_SLUGS.includes(region.stats.region_type_city.slug));
    const regionWithPrices =
        priceReportExist && PRICES_CITIES_SLUGS.includes(region.slug)
            ? PRICES_CITIES.find((city) => city.slug === region.slug)
            : PRICES_CITIES.find((city) => city.slug === region.stats?.region_type_city?.slug);
    const regionName = canUseCitySlug && !isCheapOrReady ? region.stats?.region_type_city?.name : region.name;

    return (
        <>
            {props.variant === "cheap" ? (
                <OfferLocationLinksItem
                    disableSelfLinking
                    linkText={`Tanie ${offerType ? getOfferTypeNamePlural(offerType) : "nieruchomości"} ${regionName}`}
                    url={priceSortLink}
                />
            ) : null}

            {props.variant === "ready" ? (
                <OfferLocationLinksItem
                    disableSelfLinking
                    linkText={`Gotowe ${offerType ? getOfferTypeNamePlural(offerType) : "nieruchomości"} ${regionName}`}
                    url={constructionDateReady}
                />
            ) : null}

            {/*<OfferLocationLinksItem disableSelfLinking linkText="Mieszkania z dopłatą MDM" url={rpAppPath.offerMdmList} />*/}

            {props.variant === "promotion" && promotionLink ? (
                <OfferLocationLinksItem disableSelfLinking linkText={`Oferty promocyjne ${regionName}`} url={promotionLink} />
            ) : null}

            {props.variant === "price" && regionWithPrices && offerType === OfferType.FLAT ? (
                <OfferLocationLinksItem
                    disableSelfLinking
                    linkText={`Ceny mieszkań ${regionWithPrices.name}`}
                    url={rpAppLink.prices.flats.city({regionSlug: regionWithPrices.slug})}
                />
            ) : null}

            {props.variant === "luxury" ? (
                <OfferLocationLinksItem
                    disableSelfLinking
                    linkText={`Luksusowe ${offerType ? getOfferTypeNamePlural(offerType) : "nieruchomości"} ${regionName}`}
                    url={luxuryLink}
                />
            ) : null}

            {props.variant === "vendors" && regionDataForVendorListing ? (
                <OfferLocationLinksItem disableSelfLinking linkText={`Deweloperzy ${regionDataForVendorListing.label}`} url={regionDataForVendorListing.url} />
            ) : null}
        </>
    );
});
